import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * ResizableSplit
 * - 드래그로 2개 영역 사이즈를 조절하는 공통 컴포넌트
 * - direction: 'horizontal'(좌/우) | 'vertical'(상/하)
 * - persistKey: localStorage에 사이즈(px) 저장하여 화면 재진입 시 복원
 *
 * 사용 예시:
 * <ResizableSplit direction="horizontal" persistKey="sample_lr">
 *   <Left />
 *   <Right />
 * </ResizableSplit>
 */
const ResizableSplit = ({
  direction = 'horizontal',
  initialPrimarySizePercent = 50,
  minPrimarySizePx = 100,
  minSecondarySizePx = 100,
  dividerSizePx = 6,
  persistKey,
  children,
}) => {
  const isHorizontal = direction === 'horizontal';
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const [primarySizePx, setPrimarySizePx] = useState(null);

  const [primary, secondary] = useMemo(() => {
    const arr = React.Children.toArray(children);
    return [arr[0] ?? null, arr[1] ?? null];
  }, [children]);

  const clampSize = useCallback(
    (rawSize, totalSize) => {
      const maxPrimary = Math.max(0, totalSize - minSecondarySizePx - dividerSizePx);
      const minPrimary = Math.max(0, minPrimarySizePx);
      return Math.min(Math.max(rawSize, minPrimary), maxPrimary);
    },
    [dividerSizePx, minPrimarySizePx, minSecondarySizePx]
  );

  const getTotalSize = useCallback(() => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return isHorizontal ? rect.width : rect.height;
  }, [isHorizontal]);

  // 초기 사이즈 설정 (localStorage 우선)
  useLayoutEffect(() => {
    const total = getTotalSize();
    if (!total) return;

    let nextSize = null;
    if (persistKey) {
      const stored = window.localStorage.getItem(persistKey);
      const parsed = stored ? Number(stored) : NaN;
      if (!Number.isNaN(parsed) && parsed > 0) {
        nextSize = parsed;
      }
    }

    if (nextSize == null) {
      nextSize = (total * initialPrimarySizePercent) / 100;
    }

    setPrimarySizePx(clampSize(nextSize, total));
  }, [persistKey, initialPrimarySizePercent, getTotalSize, clampSize]);

  // 창 리사이즈 시 현재 비율 유지(가능한 범위 내 clamp)
  useEffect(() => {
    const onResize = () => {
      const total = getTotalSize();
      if (!total) return;
      setPrimarySizePx((prev) => {
        if (prev == null) return prev;
        return clampSize(prev, total);
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [getTotalSize, clampSize]);

  // RealGrid 등 일부 컴포넌트는 컨테이너 크기 변경을 자동 감지하지 못합니다.
  // split 이동/초기 배치 후 window resize 이벤트를 발생시켜 레이아웃 재계산을 유도합니다.
  useEffect(() => {
    if (primarySizePx == null) return;
    const rafId = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [primarySizePx, direction]);

  const savePersist = useCallback(
    (size) => {
      if (!persistKey) return;
      try {
        window.localStorage.setItem(persistKey, String(size));
      } catch {
        // ignore
      }
    },
    [persistKey]
  );

  const updateByPointer = useCallback(
    (clientX, clientY) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = isHorizontal ? rect.width : rect.height;
      const pos = isHorizontal ? clientX - rect.left : clientY - rect.top;
      const next = clampSize(pos, total);
      setPrimarySizePx(next);
    },
    [clampSize, isHorizontal]
  );

  const onDividerMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = true;

      const onMove = (ev) => {
        if (!draggingRef.current) return;
        updateByPointer(ev.clientX, ev.clientY);
      };
      const onUp = () => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        const total = getTotalSize();
        if (total != null && primarySizePx != null) {
          savePersist(clampSize(primarySizePx, total));
        }
        window.removeEventListener('mousemove', onMove, true);
        window.removeEventListener('mouseup', onUp, true);
      };

      window.addEventListener('mousemove', onMove, true);
      window.addEventListener('mouseup', onUp, true);
    },
    [updateByPointer, getTotalSize, primarySizePx, savePersist, clampSize]
  );

  const containerStyle = {
    display: 'flex',
    flexDirection: isHorizontal ? 'row' : 'column',
    alignItems: 'stretch', // 교차축을 항상 100%로 채움
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    flex: 1,
  };

  const primaryStyle = {
    // 초기(primarySizePx가 아직 계산되기 전)에도 0크기로 렌더링되지 않도록
    // 퍼센트 기반 flex-basis를 적용합니다. (RealGrid가 0px로 초기화되는 문제 방지)
    flex:
      primarySizePx == null
        ? `0 0 ${initialPrimarySizePercent}%`
        : `0 0 ${primarySizePx}px`,
    minWidth: 0,
    minHeight: 0,
    // 패널 자체가 교차축(높이/너비)을 100%로 채우도록 강제
    width: isHorizontal ? 'auto' : '100%',
    height: isHorizontal ? '100%' : 'auto',
    overflow: 'hidden',
    // 패널 자체는 크기만 책임지고, 내부 컨텐츠는 inner wrapper가 100%를 채우도록 함
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  };

  const dividerStyle = {
    flex: `0 0 ${dividerSizePx}px`,
    background: 'rgba(0,0,0,0.06)',
    cursor: isHorizontal ? 'col-resize' : 'row-resize',
    userSelect: 'none',
  };

  const secondaryStyle = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: isHorizontal ? 'auto' : '100%',
    height: isHorizontal ? '100%' : 'auto',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  };

  // 패널 내부 컨텐츠가 “자기 높이(auto)”로 줄어들면, 내부의 height:100% 기반 컴포넌트(RealGrid 등)가 0으로 계산될 수 있습니다.
  // 그래서 패널 내부를 항상 100% 채우는 래퍼를 한 번 더 둡니다.
  const paneInnerStyle = {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  // 패널 내부의 "children 루트"가 flex로 높이를 채우지 않으면(=height:100%를 안 주면)
  // 내부 레이아웃(그리드/폼)이 0으로 계산될 수 있습니다.
  // 그래서 children을 한 번 더 flex:1 래퍼로 감싸서 자동으로 높이를 채우게 합니다.
  const paneContentStyle = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      <div style={primaryStyle}>
        <div style={paneInnerStyle}>
          <div style={paneContentStyle}>{primary}</div>
        </div>
      </div>
      <div
        role="separator"
        aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
        style={dividerStyle}
        onMouseDown={onDividerMouseDown}
      />
      <div style={secondaryStyle}>
        <div style={paneInnerStyle}>
          <div style={paneContentStyle}>{secondary}</div>
        </div>
      </div>
    </div>
  );
};

export default ResizableSplit;

